import { BaseComponent } from '../core/BaseComponent.js';
import { eventBus } from '../core/EventBus.js';

/**
 * Pagination - Handles pagination rendering and interactions
 */
export class Pagination extends BaseComponent {
    constructor(selector, options = {}) {
        super(selector, options);
        this.perPage = options.perPage || window.MAIN_TABLE_PER_PAGE || 10;
        this.currentPage = 1;
        this.totalPosts = 0;
        this.range = options.range || 1;
        this.eventNamespace = options.eventNamespace || 'default';
    }

    init() {
        if (!super.init()) return this;
        
        this.setupEventListeners();
        return this;
    }

    setupEventListeners() {
        // Listen for pagination update requests
        eventBus.on(`pagination:update:${this.eventNamespace}`, (data) => {
            this.update(data.totalPosts, data.currentPage);
        });

        // Handle pagination clicks (only for this pagination instance)
        if (this.element) {
            this.element.addEventListener('click', (e) => {
                const paginationLink = e.target.closest('[data-pagination-action]');
                if (!paginationLink) return;

                e.preventDefault();
                const page = parseInt(paginationLink.getAttribute('data-pagination-page'));
                
                if (!isNaN(page) && page > 0) {
                    this.goToPage(page);
                }
            });
        }
    }

    /**
     * Update pagination display
     */
    update(totalPosts, currentPage = 1) {
        this.totalPosts = totalPosts;
        this.currentPage = parseInt(currentPage);
        
        if (!this.element) return;
        
        this.element.innerHTML = this.render();
    }

    /**
     * Navigate to a specific page
     */
    goToPage(page) {
        const totalPages = Math.ceil(this.totalPosts / this.perPage);
        
        if (page < 1 || page > totalPages) return;
        
        this.currentPage = page;
        eventBus.emit(`pagination:pageChange:${this.eventNamespace}`, { page: this.currentPage });
    }

    /**
     * Render pagination HTML
     */
    render() {
        const totalPages = Math.ceil(this.totalPosts / this.perPage);
        
        if (totalPages <= 1) return '';

        let html = '<ul>';
        
        // Previous button
        html += this.renderPreviousButton();
        
        // Page numbers
        html += this.renderPageNumbers(totalPages);
        
        // Next button
        html += this.renderNextButton(totalPages);
        
        html += '</ul>';
        return html;
    }

    renderPreviousButton() {
        const disabled = this.currentPage <= 1 ? ' disabled' : '';
        return `
            <li class="prev_li">
                <a href="#" class="prev${disabled}" 
                   data-pagination-action="page" 
                   data-pagination-page="${this.currentPage - 1}" 
                   aria-label="Previous page">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.8333 10H4.16663M4.16663 10L9.99996 15.8334M4.16663 10L9.99996 4.16669" 
                              stroke="#A4A7AE" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Previous
                </a>
            </li>
        `;
    }

    renderNextButton(totalPages) {
        const disabled = this.currentPage >= totalPages ? ' disabled' : '';
        return `
            <li class="next_li">
                <a href="#" class="next${disabled}" 
                   data-pagination-action="page" 
                   data-pagination-page="${this.currentPage + 1}" 
                   aria-label="Next page">
                    Next
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.16675 10H15.8334M15.8334 10L10.0001 4.16669M15.8334 10L10.0001 15.8334" 
                              stroke="#A4A7AE" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </a>
            </li>
        `;
    }

    renderPageNumbers(totalPages) {
        let html = '';
        const start = Math.max(1, this.currentPage - this.range);
        const end = Math.min(totalPages, this.currentPage + this.range);

        // First page + ellipsis
        if (start > 1) {
            html += `<li><a href="#" data-pagination-action="page" data-pagination-page="1">1</a></li>`;
            if (start > 2) {
                html += `<li class="dots">…</li>`;
            }
        }

        // Page range
        for (let i = start; i <= end; i++) {
            if (i === this.currentPage) {
                html += `<li class="active"><span>${i}</span></li>`;
            } else {
                html += `<li><a href="#" data-pagination-action="page" data-pagination-page="${i}">${i}</a></li>`;
            }
        }

        // Ellipsis + last page
        if (end < totalPages) {
            if (end < totalPages - 1) {
                html += `<li class="dots">…</li>`;
            }
            html += `<li><a href="#" data-pagination-action="page" data-pagination-page="${totalPages}">${totalPages}</a></li>`;
        }

        return html;
    }
}

